import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Skeleton,
  Stack,
} from "@mui/material";
import {
  STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_QUESTION,
  STRINGS_CREATE,
  STRINGS_DELETE_QUESTION,
  STRINGS_FILTER,
  STRINGS_LOAD_MORE,
  STRINGS_QUESTIONS,
  STRINGS_SUBCATEGORIES,
} from "../../../../../data/strings/Strings";
import {
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../../../data/colors/Colors";
import { AddRounded, CategoryRounded } from "@mui/icons-material";
import { QuestionsViewModel } from "./QuestionsViewModel";
import { QuestionsListItemView } from "./questionsListItemView/QuestionsListItemView";
import {
  ROUTE_ENDPOINTS,
  ROUTE_NEW,
} from "../../../../../data/constant/RouteConstants";
import { CustomTextView } from "../../../../component/CustomTextView";
import { HeaderView } from "../../../../component/HeaderView";
import {
  provideDeleteAnswerByQuestionIdUseCase,
  provideDeleteQuestionUseCase,
  provideFilterQuestionsByUseCase,
  provideGetAllCategoriesUseCase,
  provideGetAllQuestionsUseCase,
  provideGetAnswersByListOfQuestionsIdUseCase,
  provideGetQuestionsAnswersStatsUseCase,
} from "../../../../di/UseCasesModule";
import { LoadingView } from "../../../../component/LoadingView";
import { EmptyView } from "../../../../component/EmptyView";
import { CustomAlertDialog } from "../../../../component/CustomAlertDialog";
import { useState } from "react";
import { CustomPopoverView } from "../../../../component/CustomPopoverView";
import useWindowDimensions from "../../../../../core/utils/UseWindowDimensions";
import { CustomCardView } from "../../../../component/CustomCardView";
import { FilterView } from "./questionFiltering/FilterView";
import { FilteredCategoryView } from "./questionFiltering/FilteredCategoryView";
import { SelectedCategoriesTabsView } from "./questionFiltering/SelectedCategoriesTabs";

const QuestionsListItemSkeletonView = () => {
  const { width } = useWindowDimensions();
  return (
    <CustomCardView
      padding={0}
      borderRadius={6}
      children={
        <Stack direction={"row"} width={"100%"} height={190}>
          <Stack width={140} minWidth={140}>
            <Stack spacing={1} alignItems={"center"} p={width < 1200 ? 1 : 2}>
              <Skeleton
                variant="rounded"
                width="100%"
                height={36}
                sx={{ borderRadius: 44 }}
              />
              <Skeleton
                variant="rounded"
                width="100%"
                height={36}
                sx={{ borderRadius: 44 }}
              />
            </Stack>
            <Divider flexItem />
            <Skeleton variant="rectangular" width="100%" height="100%" />
          </Stack>

          <Divider orientation="vertical" flexItem />
          <Stack
            direction={"row"}
            spacing={1}
            width={"100%"}
            alignItems={"center"}
            sx={{
              p: width < 1200 ? 1 : 2,
              overflow: "hidden",
              flex: 1,
            }}
          >
            <Stack
              width={"100%"}
              height={"100%"}
              justifyContent={"space-between"}
            >
              <Stack spacing={1} width="100%">
                <Skeleton width="90%" height={40} />
                <Skeleton width="60%" height={30} />
              </Stack>

              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width="100%"
              >
                <Skeleton width={120} height={20} />
                <Stack direction="row" spacing={1}>
                  <Skeleton width={30} height={20} />
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton width={30} height={20} />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack
            p={width < 1200 ? 1 : 2}
            justifyContent="center"
            alignItems="center"
          >
            <Skeleton width={40} height={30} />
          </Stack>
        </Stack>
      }
    />
  );
};

export function QuestionsView() {
  const viewModel = QuestionsViewModel(
    provideFilterQuestionsByUseCase(),
    provideDeleteQuestionUseCase(),
    provideDeleteAnswerByQuestionIdUseCase(),
    provideGetAllCategoriesUseCase(),
    provideGetAllQuestionsUseCase(),
    provideGetAnswersByListOfQuestionsIdUseCase(),
    provideGetQuestionsAnswersStatsUseCase(),
  );

  const { width } = useWindowDimensions();

  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<
    string | undefined
  >();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    handleChange(undefined, 0);
  };

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const handleChange = (
    event: React.SyntheticEvent | undefined = undefined,
    newValue: number,
  ) => {
    event?.preventDefault();
    setSelectedTabIndex(newValue);
    if (viewModel.hasNoFilter()) return;
  };

  return (
    <Box>
      {viewModel.categories && (
        <CustomPopoverView
          layout={
            <FilterView
              onClose={handleClose}
              categories={viewModel.categories}
            />
          }
          anchorEl={anchorEl}
          onClose={handleClose}
        />
      )}
      <Stack>
        <HeaderView
          title={STRINGS_QUESTIONS}
          isLayoutFullWidthInSmallerScreens
          titleLayout={
            viewModel.questionsAnswersCount && (
              <CustomTextView
                text={`(total: ${viewModel.questionsAnswersCount.questionsCount})`}
                variant="subtitle1"
                fontWeight="500"
                textColor={COLOR_LIGHT_GRAY}
              />
            )
          }
          layout={
            <Stack
              direction={"row"}
              display={"flex"}
              justifyContent={width < 900 ? "start" : "end"}
              spacing={1}
            >
              <Button
                onClick={handleClick}
                variant={viewModel.hasNoFilter() ? "outlined" : "contained"}
                disabled={
                  viewModel.categories && viewModel.categories.length > 0
                    ? false
                    : true
                }
                sx={{
                  borderRadius: 22,
                  borderColor: COLOR_LESS_BLACK,
                  "&:hover": {
                    borderColor: COLOR_LESS_BLACK,
                    background: COLOR_LIGHT_GRAY,
                  },
                  filter:
                    viewModel.categories && viewModel.categories.length > 0
                      ? undefined
                      : "blur(6px)",
                  background: viewModel.hasNoFilter()
                    ? "transparent"
                    : COLOR_LESS_BLACK,
                }}
              >
                <CategoryRounded
                  fontSize="medium"
                  sx={{
                    color: viewModel.hasNoFilter()
                      ? COLOR_LESS_BLACK
                      : COLOR_WHITE,
                    marginRight: 1,
                  }}
                />
                <CustomTextView
                  text={STRINGS_FILTER}
                  fontWeight="bold"
                  fontSize={16}
                  props={{ textTransform: "none" }}
                  textColor={
                    viewModel.hasNoFilter() ? COLOR_LESS_BLACK : COLOR_WHITE
                  }
                />
              </Button>
              <Button
                href={`${ROUTE_ENDPOINTS.HOME_QUESTIONS}/${ROUTE_NEW}`}
                LinkComponent={"a"}
                variant="outlined"
                sx={{
                  borderRadius: 22,
                  borderColor: COLOR_LESS_BLACK,
                  "&:hover": {
                    borderColor: COLOR_LESS_BLACK,
                  },
                }}
              >
                <AddRounded
                  fontSize="medium"
                  sx={{ color: COLOR_LESS_BLACK }}
                />
                <CustomTextView
                  text={STRINGS_CREATE}
                  fontWeight="bold"
                  fontSize={16}
                  props={{ textTransform: "none" }}
                />
              </Button>
            </Stack>
          }
        />
        <Container maxWidth={false}>
          <>
            {viewModel.allQuestionsLoading ? (
              <Grid container spacing={2} pt={3} pb={3}>
                {Array.from(new Array(8)).map((_, index) => (
                  <Grid size={{ xs: 12, xl: 6 }} key={index}>
                    <QuestionsListItemSkeletonView />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <>
                {viewModel.hasNoFilter() ? (
                  <>
                    {viewModel.allQuestionsLoading &&
                    viewModel.allQuestions?.length === 0 ? (
                      <LoadingView />
                    ) : (
                      <>
                        <Grid container spacing={2} pt={3} pb={3}>
                          {viewModel.allQuestions &&
                          viewModel.allQuestions.length > 0 ? (
                            viewModel.allQuestions.map((question, index) => {
                              return (
                                <Grid
                                  size={{ xs: 12, xl: 6 }}
                                  key={question._id}
                                  sx={{ maxWidth: "97vw" }}
                                >
                                  <QuestionsListItemView
                                    question={question}
                                    onDeleteClicked={() => {
                                      setShowDeleteAlertDialog(true);
                                      setSelectedQuestionId(question._id);
                                    }}
                                    index={index}
                                    href={`${ROUTE_ENDPOINTS.HOME_QUESTIONS}/${question._id}`}
                                    cardBgColor={COLOR_VERY_LIGHT_GRAY}
                                  />
                                </Grid>
                              );
                            })
                          ) : (
                            <EmptyView />
                          )}
                          <Grid
                            alignItems={"center"}
                            justifyContent={"center"}
                            display={"flex"}
                            size={12}
                          >
                            {viewModel.isLoadingMore ? (
                              <LoadingView minHeight="5vh" />
                            ) : (
                              <>
                                {viewModel.showLoadMoreButton && (
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 22,
                                      textTransform: "none",
                                      borderColor: COLOR_LESS_BLACK,
                                      "&:hover": {
                                        borderColor: COLOR_LESS_BLACK,
                                        background: COLOR_LIGHT_GRAY,
                                      },
                                    }}
                                    onClick={viewModel.fetchAllQuestions}
                                  >
                                    <CustomTextView
                                      text={STRINGS_LOAD_MORE}
                                      variant="h6"
                                      fontWeight="bold"
                                    />
                                  </Button>
                                )}
                              </>
                            )}
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                ) : (
                  <Grid container spacing={2} pt={3} pb={3}>
                    <Grid
                      size={12}
                      sx={{
                        maxWidth: "95vw",
                      }}
                    >
                      <SelectedCategoriesTabsView
                        selectedTabIndex={selectedTabIndex}
                        handleChange={handleChange}
                        selectedCategories={viewModel.getSelectedCategoriesList()}
                      />
                    </Grid>

                    <Grid size={12}>
                      <Divider />
                    </Grid>
                    <Grid size={12}>
                      <FilteredCategoryView
                        selectedCategoryId={
                          viewModel.categories
                            ? viewModel.getSelectedCategoriesList()[
                                selectedTabIndex
                              ]._id
                            : undefined
                        }
                        questions={
                          viewModel.categories
                            ? viewModel.getSelectedCategoriesList()[
                                selectedTabIndex
                              ].questions
                            : undefined
                        }
                        getQuestions={viewModel.fetchQuestionsOfCategory}
                        setSelectedQuestionId={setSelectedQuestionId}
                        setShowDeleteAlertDialog={setShowDeleteAlertDialog}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </>
        </Container>
      </Stack>
      <CustomAlertDialog
        title={STRINGS_DELETE_QUESTION}
        description={STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_QUESTION}
        open={showDeleteAlertDialog}
        handleNegativeButtonClick={() => setShowDeleteAlertDialog(false)}
        handlePositiveButtonClick={() => {
          setShowDeleteAlertDialog(false);
          if (selectedQuestionId) viewModel.deleteQuestion(selectedQuestionId);
        }}
      />
    </Box>
  );
}
